// src/pages/negocio/PanelNegocio.js
//
// Panel del tendero: métricas del rango, distribuciones (estados,
// métodos de pago), cumplimiento, domiciliarios, resumen de fiados.
// Todo real desde GET /api/holaveci/panel/negocio.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Grid, Chip, Alert,
  CircularProgress, Paper, LinearProgress, TextField, Divider,
} from '@mui/material';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon   from '@mui/icons-material/ReceiptLong';
import CreditScoreIcon   from '@mui/icons-material/CreditScore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import { panel as apiPanel } from '../../api/panelApi';
import { formatoCOP } from '../../utils/format';

const COLORES_ESTADO = {
  nuevo:       '#9E9E9E',
  preparacion: '#0288D1',
  en_ruta:     '#F57C00',
  entregado:   '#388E3C',
  cancelado:   '#C62828',
};
const COLORES_PAGO = ['#2E7D32', '#F57C00', '#0288D1', '#7B1FA2', '#795548'];

const hoy = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function PanelNegocio() {
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await apiPanel.negocio(desde, hasta);
      setData(r);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cargar el panel.');
    } finally { setCargando(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const preset = (label, from, to) => (
    <Chip label={label} onClick={() => { setDesde(from); setHasta(to); }} size="small" variant="outlined" clickable />
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Panel</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Tu negocio en números</Typography>
      </Box>

      {/* Filtro de rango */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {preset('Hoy',       hoy(),        hoy())}
              {preset('7 días',    haceDias(7),  hoy())}
              {preset('30 días',   haceDias(30), hoy())}
              {preset('Este año',  new Date().getFullYear() + '-01-01', hoy())}
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth type="date" label="Desde" value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth type="date" label="Hasta" value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <>
          {/* KPIs principales */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <KPI
                icono={<TrendingUpIcon />}
                label="Total vendido"
                valor={formatoCOP(data.totalVendido || data.TotalVendido || 0)}
                color="success.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPI
                icono={<ReceiptLongIcon />}
                label="Pedidos"
                valor={data.cantidadPedidos ?? data.CantidadPedidos ?? 0}
                color="primary.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPI
                icono={<TrendingUpIcon />}
                label="Ticket promedio"
                valor={formatoCOP(data.ticketPromedio || data.TicketPromedio || 0)}
                color="info.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPI
                icono={<LocalShippingIcon />}
                label="En curso"
                valor={data.pedidosEnCurso ?? data.PedidosEnCurso ?? 0}
                color="warning.main"
              />
            </Grid>
          </Grid>

          {/* Cumplimiento */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontFamily: 'Fraunces', mb: 2 }}>Cumplimiento de entrega</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {(data.porcentajeCumplimiento || data.PorcentajeCumplimiento || 0).toFixed(1)}% a tiempo
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={data.porcentajeCumplimiento || data.PorcentajeCumplimiento || 0}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={<span>✓</span>}
                    label={`${data.entregadosATiempo ?? data.EntregadosATiempo ?? 0} a tiempo`}
                    color="success" variant="outlined"
                  />
                  <Chip
                    icon={<span>⏱</span>}
                    label={`${data.entregadosConDemora ?? data.EntregadosConDemora ?? 0} con demora`}
                    color="warning" variant="outlined"
                  />
                </Stack>
              </Box>
            </CardContent>
          </Card>

          {/* Distribuciones */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontFamily: 'Fraunces', mb: 2 }}>Pedidos por estado</Typography>
                  <PieDistribucion
                    data={data.porEstado || data.PorEstado || []}
                    colores={COLORES_ESTADO}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontFamily: 'Fraunces', mb: 2 }}>Método de pago</Typography>
                  <PieDistribucion
                    data={data.porMetodoPago || data.PorMetodoPago || []}
                    coloresList={COLORES_PAGO}
                    mostrarTotal
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Domiciliarios */}
          {(data.domiciliarios || data.Domiciliarios || []).length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontFamily: 'Fraunces', mb: 2 }}>Domiciliarios</Typography>
                <Stack spacing={1.5}>
                  {(data.domiciliarios || data.Domiciliarios).map((d, i) => (
                    <Box key={i} sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      p: 1.5, borderRadius: 1, bgcolor: '#FBF7F0', flexWrap: 'wrap', gap: 1,
                    }}>
                      <Box>
                        <Typography variant="subtitle2">{d.nombre || d.Nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {d.entregados || d.Entregados} entregas · ~{d.minutosPromedioEntrega || d.MinutosPromedioEntrega} min
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Chip size="small" label={`Efectivo ${formatoCOP(d.efectivoRecaudado || d.EfectivoRecaudado || 0)}`} color="success" variant="outlined" />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Fiados */}
          {(data.fiados || data.Fiados) && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontFamily: 'Fraunces', mb: 2 }}>
                  <CreditScoreIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Fiados
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(data.fiados || data.Fiados || {}).map(([k, v]) => {
                    if (typeof v !== 'number' && typeof v !== 'string') return null;
                    return (
                      <Grid item xs={6} md={3} key={k}>
                        <Paper sx={{ p: 2, bgcolor: '#FBF7F0' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {k.replace(/([A-Z])/g, ' $1').trim()}
                          </Typography>
                          <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>
                            {typeof v === 'number' && (k.toLowerCase().includes('total') || k.toLowerCase().includes('saldo'))
                              ? formatoCOP(v)
                              : v}
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Stack>
  );
}

function KPI({ icono, label, valor, color }) {
  return (
    <Paper sx={{ p: 2.5, height: '100%', borderLeft: 4, borderColor: color }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color }}>
        {icono}
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontFamily: 'Fraunces', color: 'text.primary' }}>
        {valor}
      </Typography>
    </Paper>
  );
}

function PieDistribucion({ data, colores = {}, coloresList = [], mostrarTotal = false }) {
  const normalizado = data.map((d) => ({
    name: d.etiqueta || d.Etiqueta,
    codigo: d.codigo || d.Codigo,
    value: d.cantidad || d.Cantidad || 0,
    total: d.total || d.Total || 0,
  })).filter(d => d.value > 0);

  if (normalizado.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
        Sin datos en el rango seleccionado.
      </Typography>
    );
  }

  return (
    <Box sx={{ height: 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={normalizado}
            dataKey="value"
            nameKey="name"
            cx="50%" cy="50%"
            outerRadius={80}
            label={(e) => e.name}
          >
            {normalizado.map((entry, i) => (
              <Cell
                key={i}
                fill={colores[entry.codigo] || coloresList[i % coloresList.length] || '#666'}
              />
            ))}
          </Pie>
          <Tooltip formatter={(v, _n, p) => {
            if (mostrarTotal && p?.payload?.total) return [`${v} pedidos · ${p.payload.total.toLocaleString('es-CO')} COP`, p.payload.name];
            return [`${v} pedidos`, p.payload.name];
          }} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}
